/**
 * Couche d'écriture du miroir — la seule autorisée à toucher Supabase en
 * écriture. Elle existe pour trois raisons, chacune correspondant à une
 * façon dont la synchro multi-poste casse silencieusement.
 *
 * 1. `updated_at` + `device_id` sur CHAQUE écriture.
 *    Les deux colonnes sont `not null` sans default, et c'est le couple qui
 *    arbitre le last-write-wins côté Mac (`mirror.rs`). Une écriture sans
 *    `updated_at` frais n'écrase rien : elle est refusée par le garde LWW au
 *    prochain pull, sans que rien ne le signale.
 *
 * 2. JAMAIS de DELETE — uniquement une pierre tombale `deleted_at`.
 *    Le Mac tire les nouveautés avec `?server_seq=gt.<cursor>`. Une ligne
 *    effacée en dur disparaît du curseur : le Mac n'apprend jamais la
 *    suppression et garde la fiche pour toujours. La tombstone, elle,
 *    remonte au-dessus du curseur et se propage.
 *
 * 3. Une file persistée.
 *    Le cas d'usage est le canapé et la salle d'attente. On écrit en
 *    optimiste dans l'état local, on met l'opération en file IDB, et on
 *    pousse quand le réseau revient. Rien n'est perdu si l'app est tuée.
 *
 * Les opérations sont des assignations de champs, donc idempotentes : deux
 * écritures sur la même ligne fusionnent au lieu de se succéder.
 */

import { get, set } from "idb-keyval";
import { supabase } from "./client";

const QUEUE_KEY = "mailfox:crm-write-queue";
const DEVICE_KEY = "mailfox:crm-device-id";
/** Au-delà, on arrête de réessayer en boucle et on rend l'échec visible. */
const MAX_ATTEMPTS = 5;

export type Op =
  /** UPDATE ciblé par clé primaire. La ligne existe déjà. */
  | {
      kind: "patch";
      table: string;
      match: Record<string, string>;
      values: Record<string, unknown>;
    }
  /** INSERT ... ON CONFLICT DO UPDATE. Pour les lignes qui peuvent manquer. */
  | {
      kind: "upsert";
      table: string;
      onConflict: string;
      row: Record<string, unknown>;
    };

interface Pending {
  key: string;
  op: Op;
  attempts: number;
}

export const writeState = $state<{
  pending: number;
  flushing: boolean;
  error: string | null;
}>({ pending: 0, flushing: false, error: null });

/**
 * Identité de cet appareil dans le last-write-wins. Stable pour la durée de
 * l'installation : c'est ce qui départage deux écritures d'horodatage égal.
 */
export function deviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = `pwa-${crypto.randomUUID()}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export function nowIso(): string {
  return new Date().toISOString();
}

/** Les colonnes de contrôle que le miroir exige de toute écriture. */
export function stamp(): { updated_at: string; device_id: string } {
  return { updated_at: nowIso(), device_id: deviceId() };
}

// ─── File ──────────────────────────────────────────────────────────────────

let queue: Pending[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Deux opérations qui visent la même ligne de la même table partagent cette
 * clé, et fusionnent au lieu de s'empiler. Sans ça, cocher cinq étiquettes
 * d'affilée hors ligne enverrait cinq requêtes là où une suffit.
 */
function keyOf(op: Op): string {
  if (op.kind === "patch") {
    const m = Object.entries(op.match)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join(",");
    return `${op.table}|${m}`;
  }
  const pk = op.onConflict
    .split(",")
    .map((c) => `${c}=${String(op.row[c])}`)
    .join(",");
  return `${op.table}|${pk}`;
}

async function persist(): Promise<void> {
  writeState.pending = queue.length;
  try {
    await set(QUEUE_KEY, queue);
  } catch (e) {
    console.warn("crm: persist queue failed:", e);
  }
}

function enqueue(op: Op): void {
  const key = keyOf(op);
  const existing = queue.find((p) => p.key === key);
  if (existing && existing.op.kind === op.kind) {
    // Fusion champ à champ — la dernière valeur de chaque champ gagne, et
    // les champs touchés plus tôt survivent.
    if (existing.op.kind === "patch" && op.kind === "patch") {
      existing.op.values = { ...existing.op.values, ...op.values };
    } else if (existing.op.kind === "upsert" && op.kind === "upsert") {
      existing.op.row = { ...existing.op.row, ...op.row };
    }
    existing.attempts = 0;
  } else {
    queue.push({ key, op, attempts: 0 });
  }
  void persist();
  scheduleFlush();
}

/**
 * UPDATE d'une ligne existante. `values` est complété par `updated_at` et
 * `device_id` — ne les passe pas toi-même.
 */
export function patch(
  table: string,
  match: Record<string, string>,
  values: Record<string, unknown>,
): void {
  enqueue({ kind: "patch", table, match, values: { ...values, ...stamp() } });
}

/** INSERT/UPDATE d'une ligne qui peut ne pas exister (les liens étiquette). */
export function upsert(
  table: string,
  onConflict: string,
  row: Record<string, unknown>,
): void {
  enqueue({ kind: "upsert", table, onConflict, row: { ...row, ...stamp() } });
}

/**
 * Suppression au sens du miroir : une pierre tombale, jamais un DELETE.
 * Voir l'en-tête de ce fichier, raison n°2.
 */
export function tombstone(table: string, match: Record<string, string>): void {
  patch(table, match, { deleted_at: nowIso() });
}

/** Annule une tombstone — le pendant exact de `tombstone`. */
export function revive(table: string, match: Record<string, string>): void {
  patch(table, match, { deleted_at: null });
}

function scheduleFlush(): void {
  if (flushTimer) return;
  // Un court délai laisse les taps rapides fusionner avant le départ.
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushWrites();
  }, 400);
}

async function run(op: Op): Promise<void> {
  if (op.kind === "patch") {
    const { error } = await supabase.from(op.table).update(op.values).match(op.match);
    if (error) throw new Error(`${op.table}: ${error.message}`);
  } else {
    const { error } = await supabase
      .from(op.table)
      .upsert(op.row, { onConflict: op.onConflict });
    if (error) throw new Error(`${op.table}: ${error.message}`);
  }
}

/**
 * Vide la file, dans l'ordre. Un échec réseau laisse l'opération en place et
 * s'arrête là : l'ordre compte (une société doit exister avant son lien), et
 * insister sur un réseau coupé ne fait que brûler la batterie.
 */
export async function flushWrites(): Promise<void> {
  if (writeState.flushing || queue.length === 0) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;

  writeState.flushing = true;
  try {
    while (queue.length > 0) {
      const item = queue[0];
      try {
        await run(item.op);
        queue.shift();
        writeState.error = null;
        await persist();
      } catch (e) {
        item.attempts += 1;
        const msg = e instanceof Error ? e.message : String(e);
        if (item.attempts >= MAX_ATTEMPTS) {
          // Un refus durable (RLS, contrainte) ne doit pas bloquer le reste
          // de la file pour toujours — mais il ne doit pas non plus
          // disparaître en silence.
          writeState.error = `Écriture refusée — ${msg}`;
          queue.shift();
        } else {
          writeState.error = msg;
        }
        await persist();
        break;
      }
    }
  } finally {
    writeState.flushing = false;
  }
}

/** À appeler une fois au boot : relit la file laissée par la session passée. */
export async function initWriteQueue(): Promise<void> {
  try {
    queue = (await get<Pending[]>(QUEUE_KEY)) ?? [];
  } catch (e) {
    console.warn("crm: read queue failed:", e);
    queue = [];
  }
  writeState.pending = queue.length;
  window.addEventListener("online", () => void flushWrites());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void flushWrites();
  });
  void flushWrites();
}
